import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useSite } from "../context/SiteContext";
import { fetchProducts, fetchEvents } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Marquee } from "../components/Marquee";
import { langField } from "../lib/i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1748302913353-90a60a1cba9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxwdW5rJTIwcm9jayUyMG11c2ljJTIwY3Jvd2QlMjBkYXJrfGVufDB8fHx8MTc3ODI4NTE3OHww&ixlib=rb-4.1.0&q=85";
const LOOK_1 =
  "https://images.unsplash.com/photo-1738636591778-b4e73eb2748f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxzdHJlZXR3ZWFyJTIwaGVhdnklMjBtZXRhbCUyMGJsYWNrJTIwc2hpcnQlMjBmYXNoaW9ufGVufDB8fHx8MTc3ODI4NTE5OXww&ixlib=rb-4.1.0&q=85";
const LOOK_2 =
  "https://images.unsplash.com/photo-1754472390008-e1ddbed633ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxzdHJlZXR3ZWFyJTIwaGVhdnklMjBibGFjayUyMHNoaXJ0JTIwZmFzaGlvbnxlbnwwfHx8fDE3NzgyODUxOTl8MA&ixlib=rb-4.1.0&q=85";
const LOOK_3 =
  "https://images.unsplash.com/photo-1609269879435-6d188776e3aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHx3cmVzdGxpbmclMjBtYXNrJTIwbHVjaGElMjBsaWJyZXxlbnwwfHx8fDE3NzgyODUxNzh8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t, lang } = useApp();
  const { s, img, list } = useSite();
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => {});
    fetchEvents().then(setEvents).catch(() => {});
  }, []);

  const featured = products.slice(0, 6);
  const heroImg = img("hero_image", HERO_IMG);
  const heroTitle = s("hero_title", lang === "pt" ? "VESTE O\nCULTO." : "WEAR THE\nCULT.");
  const lines = heroTitle.split(/\n|\//).map((x) => x.trim()).filter(Boolean);

  return (
    <div data-testid="page-home">
      <section className="relative overflow-hidden border-b border-kult-border">
        <div className="absolute inset-0">
          <img src={heroImg} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-5 md:px-10 pt-20 pb-32 md:pt-32 md:pb-44">
          <p className="font-mono text-xs md:text-sm tracking-[0.3em] text-kult-yellow mb-6">
            ⚡ {s("hero_tagline", t.hero.tagline)}
          </p>
          <h1 className="font-display text-[18vw] sm:text-[14vw] md:text-[10vw] lg:text-[9rem] leading-[0.85] tracking-tighter" data-testid="hero-headline">
            <span className="text-white">{lines[0] || ""}</span>
            <br />
            <span className="text-kult-pink">{lines[1] || ""}</span>
          </h1>
          <p className="mt-8 max-w-xl text-base md:text-lg text-kult-muted">{s("hero_sub", t.hero.subline)}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/shop" className="bg-kult-pink text-black px-8 py-4 font-display text-xl tracking-tight kult-shadow-yellow hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all" data-testid="hero-cta-shop">
              {s("hero_cta_shop", t.hero.cta_shop)} <ArrowRight className="inline ml-2" size={18} />
            </Link>
            <Link to="/events" className="border-2 border-white text-white px-8 py-4 font-display text-xl tracking-tight hover:bg-white hover:text-black transition-colors" data-testid="hero-cta-events">
              {s("hero_cta_events", t.hero.cta_events)}
            </Link>
          </div>
        </div>
      </section>

      <Marquee items={list("marquee_main", ["WRESTLING", "MUSIC", "EVENTS", "PORTO", "INVICTA"])} bg="bg-kult-pink" />

      <section className="max-w-[1600px] mx-auto px-5 md:px-10 py-20" data-testid="home-featured">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ 01</p>
            <h2 className="font-display text-5xl md:text-7xl mt-2">{s("featured_title", t.sections.featured)}</h2>
            <p className="text-kult-muted mt-2">{s("featured_sub", t.sections.featured_sub)}</p>
          </div>
          <Link to="/shop" className="font-mono text-xs tracking-[0.25em] kult-link self-start md:self-auto" data-testid="home-featured-view-all">
            {t.product.view_all} →
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      <section className="bg-kult-black2 border-y border-kult-border" data-testid="home-collections">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-20">
          <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ 02</p>
          <h2 className="font-display text-5xl md:text-7xl mt-2 mb-10">{s("collections_title", t.sections.shop_collections)}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { slug: "wrestling", img: img("collection_wrestling_image", LOOK_3), label_en: "WRESTLING", label_pt: "WRESTLING" },
              { slug: "music", img: img("collection_music_image", HERO_IMG), label_en: "MUSIC", label_pt: "MÚSICA" },
              { slug: "events", img: img("collection_events_image", LOOK_1), label_en: "EVENTS", label_pt: "EVENTOS" },
            ].map((c) => (
              <Link key={c.slug} to={`/shop?collection=${c.slug}`} className="group relative aspect-[4/5] overflow-hidden border border-kult-border hover:border-kult-pink" data-testid={`collection-card-${c.slug}`}>
                <img src={c.img} alt={c.label_en} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="font-display text-5xl text-white group-hover:text-kult-pink transition-colors">{lang === "pt" ? c.label_pt : c.label_en}</h3>
                  <p className="font-mono text-xs text-kult-yellow mt-2 tracking-[0.25em]">SHOP →</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-kult-black2 border-y border-kult-border" data-testid="home-events">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ 03</p>
              <h2 className="font-display text-5xl md:text-7xl mt-2">{s("events_title", t.sections.events)}</h2>
              <p className="text-kult-muted mt-2">{s("events_sub", t.sections.events_sub)}</p>
            </div>
            <Link to="/events" className="font-mono text-xs tracking-[0.25em] kult-link">ALL EVENTS →</Link>
          </div>
          <div className="border border-kult-border divide-y divide-kult-border" data-testid="events-list">
            {events.slice(0, 4).map((e) => {
              const d = new Date(e.date);
              return (
                <Link key={e.id} to="/events" className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 p-5 hover:bg-kult-pink hover:text-black transition-colors group" data-testid={`event-row-${e.id}`}>
                  <span className="font-mono text-sm tracking-[0.2em] md:w-44">
                    {d.toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase()}
                  </span>
                  <span className="font-display text-2xl md:text-3xl flex-1">{langField(e, "title", lang)}</span>
                  <span className="font-mono text-xs tracking-[0.25em] text-kult-muted group-hover:text-black">{e.city.toUpperCase()} / {e.venue}</span>
                  <Zap size={18} className="text-kult-yellow group-hover:text-black" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
